import type { MyFamilyMember, MyFamilyResponse } from '@/api/family'
import type { AutoOrderAutoReloadAlert, FamilySubscriptionWire } from '@/api/family-subscriptions'

export function familyMembers(data: MyFamilyResponse | null | undefined): MyFamilyMember[] {
  return (data?.me?.ownedAccounts ?? [])
    .filter((a) => a.kind === 'HOUSEHOLD' && !a.isArchived)
    .flatMap((a) => a.members.filter((m) => !m.isArchived))
}

/** Unknown/unconfigured campuses keep the existing permissive behavior. */
export function memberAcceptsParentOrders(member: MyFamilyMember): boolean {
  if (!member.user || member.isArchived) {
    return false
  }
  const campuses = (member.user?.memberships ?? []).filter((m) => !m.isArchived && m.location?.type === 'SCHOOL_CAMPUS')
  return campuses.length === 0 || campuses.some((m) => m.location?.parentOrderingEnabled !== false)
}

/** A child can attend two campuses: eligibility belongs to the subscription's campus. */
export function subscriptionAcceptsParentOrders(
  sub: Pick<FamilySubscriptionWire, 'studentUserId' | 'schoolId'>,
  members: readonly MyFamilyMember[],
): boolean {
  const member = members.find((m) => m.user?.id === sub.studentUserId)
  const membership = member?.user?.memberships?.find((m) => !m.isArchived && m.location?.id === sub.schoolId)
  return membership?.location?.parentOrderingEnabled !== false
}

export function eligibleAutoReloadAlerts(
  alerts: readonly AutoOrderAutoReloadAlert[],
  subscriptions: readonly FamilySubscriptionWire[],
  members: readonly MyFamilyMember[],
): AutoOrderAutoReloadAlert[] {
  const eligible = new Set(
    subscriptions.filter((sub) => subscriptionAcceptsParentOrders(sub, members)).map((sub) => sub.id),
  )
  return alerts
    .map((alert) => ({ ...alert, subscriptions: alert.subscriptions.filter((sub) => eligible.has(sub.id)) }))
    .filter((alert) => alert.subscriptions.length > 0)
}
